import { PropsWithChildren, ReactNode, forwardRef, useCallback } from 'react'
import { ChevronLeftIcon, XCloseIcon } from '@systemeio/icons'
import { twJoin, twMerge } from 'tailwind-merge'
import { useOnSwipe } from 'shared/hooks/use-on-swipe'
import { useToggle } from 'shared/hooks/use-toggle'
import { DrawerPositionEnum } from './types'

type DrawerProps = PropsWithChildren & {
  position?: DrawerPositionEnum
  open: boolean
  collapsible?: boolean
  title?: ReactNode
  onClose?: () => void
  controlled?: boolean
  className?: string
}

const Drawer = forwardRef<HTMLDivElement, DrawerProps>(function Drawer(
  {
    position = DrawerPositionEnum.Right,
    open = false,
    collapsible = false,
    children,
    title,
    onClose,
    controlled,
    className,
  },
  ref,
) {
  const [collapsed, toggleCollapsed, setCollapsed] = useToggle()

  const TitleWrapper = typeof title === 'string' ? 'h2' : 'div'

  const handleSwipeLeft = useCallback(() => collapsible && setCollapsed(true), [collapsible])

  const handleSwipeRight = useCallback(() => collapsible && setCollapsed(false), [collapsible])

  useOnSwipe({ direction: 'left', onSwipe: handleSwipeLeft })
  useOnSwipe({ direction: 'right', onSwipe: handleSwipeRight })

  return (
    <div
      ref={ref}
      data-testid="drawer"
      className={twMerge(
        'fixed bottom-0 z-10 h-full w-0 bg-bg-primary transition-all',
        position === DrawerPositionEnum.Right ? 'right-0' : 'left-0',
        open && !collapsed && `w-80 md:w-96`,
        className,
      )}
    >
      {collapsible && open && (
        <button
          type="button"
          className={twJoin(
            'absolute top-1/2 -translate-y-1/2',
            position === DrawerPositionEnum.Right ? '-left-5' : `-right-5 rotate-180`,
          )}
          onClick={toggleCollapsed}
        >
          <ChevronLeftIcon className="size-5" />
        </button>
      )}
      <div className="flex customScroll h-full flex-col gap-5 overflow-x-hidden overflow-y-auto p-5">
        <div className="grid w-full grid-cols-[1fr_10%]">
          {title === undefined || title === null ? null : <TitleWrapper>{title}</TitleWrapper>}
          {!controlled && (
            <button type="button" onClick={onClose} className="justify-self-end">
              <XCloseIcon className="size-5 text-text-primary" />
            </button>
          )}
        </div>
        <div>{children}</div>
      </div>
    </div>
  )
})

export default Drawer
